import Vue from 'vue';
import {
	URLS,
	apiAxios,
	displayError,
	changeRouteParam,
	sleep
} from '../../../js/buyout';
import { config } from '../../config/buyout';
import { scrollTo as VueScrollTo } from 'vue-scrollto';
import i18n from '../../plugins/i18n';
import { EventBus } from '../../event_bus';
import { ACTION_TYPES } from './_action-types';
import { MUTATION_TYPES } from './_mutation-types';
import _map from 'lodash/map';
import _filter from 'lodash/filter';
import _intersection from 'lodash/intersection';
import _includes from 'lodash/includes';
import _sortBy from 'lodash/sortBy';
import _reverse from 'lodash/reverse';
import _uniq from 'lodash/uniq';
import _isEmpty from 'lodash/isEmpty';
import { GETTER_TYPES } from './_getter-types';

export const actions = {
	async [ACTION_TYPES.LOAD_DATA](
		store,
		{ url, displayErrorNotification = true, params }
	) {
		const result = { response: null, error: null }
		try {
			result.response = await apiAxios.get(url, { params, headers: {'Accept-Language': i18n.locale} });
		} catch (err) {
			result.error = err;
			if (displayErrorNotification)
				displayError(err, i18n.t('buyout.error.common'));
			else console.error(err);
		}
		return result;
	},
	async [ACTION_TYPES.LOAD_CAMPAIGN]({ commit, dispatch, state }) {
		let url = URLS.CAMPAIGN;
		let { response } = await dispatch(ACTION_TYPES.LOAD_DATA, {
			url: `${url}/${state.campaignIdentifier}`
		});

		if (response) {
			commit(MUTATION_TYPES.SET_CAMPAIGN, response.data);
		}
	},
	async [ACTION_TYPES.LOAD_COMPONENT_DATA]({ dispatch }, payload) {
		const { type, identifier } = payload;
		let url =
			type === 'text' ? URLS.EDITOR_TEXT_BLOCK : URLS.EDITOR_BANNER_BLOCK;
		return await dispatch(ACTION_TYPES.LOAD_DATA, {
			url: `${url}/${identifier}`
		});
	},
	async [ACTION_TYPES.LOAD_FAQS]({ dispatch }, payload) {
		let url = URLS.ACTIVE_FAQS;
		let { response } = await dispatch(ACTION_TYPES.LOAD_DATA, {
			url: `${url}`,
			params: {
				type_faq__identifier: payload.identifier
			}
		});
		return response ? response.data : [];
	},
	async [ACTION_TYPES.LOAD_HOW_IT_WORKS]({ state, dispatch }) {
		let url = URLS.HOW_IT_WORKS;
		let { response } = await dispatch(ACTION_TYPES.LOAD_DATA, {
			url: `${url}/${state.campaignIdentifier}`
		});

		return response ? response.data : [];
	},
	async [ACTION_TYPES.LOAD_CATEGORIES]({ commit, dispatch, state }) {
		commit(MUTATION_TYPES.SET_LOADING_CATEGORIES, !state.loadingCategories);

		const { response, error } = await dispatch(ACTION_TYPES.LOAD_DATA, {
			url: URLS.PRODUCT_CATEGORIES
		});
		if (error) return;

		commit(MUTATION_TYPES.SET_CATEGORIES, response.data);
		commit(MUTATION_TYPES.SET_LOADING_CATEGORIES, !state.loadingCategories);

		if (
			config.isURLHandlingEnabled &&
			config.router.currentRoute.params.category
		) {
			let cat_url = config.accessoryCategoryChangedToSmartWatch ? (
				(config.router.currentRoute.params.category === 'chytre-hodinky') ? 'prislusenstvi' : config.router.currentRoute.params.category)
				: config.router.currentRoute.params.category
			let sel_cat = state.categories.find(
				cat => cat.seo_name === cat_url
			);

			if (sel_cat) {
				commit(MUTATION_TYPES.SET_SELECTED_CATEGORY, sel_cat || null);
				await dispatch(ACTION_TYPES.LOAD_BRANDS);
			} else {
				commit(MUTATION_TYPES.RESET_BRANDS);
				commit(MUTATION_TYPES.RESET_MODELS);
				commit(MUTATION_TYPES.RESET_COLORS);

				if (!state.userBuyoutCode) await dispatch(ACTION_TYPES.RESET_APPRAISAL);

				if (config.isURLHandlingEnabled) {
					await changeRouteParam({
						category: undefined,
						brand: undefined,
						model: undefined,
						color: undefined
					});
				}
			}
		}
	},
	async [ACTION_TYPES.CHANGE_CATEGORY]({ dispatch, state }) {
		if (config.isURLHandlingEnabled) {
			await changeRouteParam({
				category: config.accessoryCategoryChangedToSmartWatch ? (
					(state.selectedCategory.seo_name === 'prislusenstvi') ? 'chytre-hodinky' : state.selectedCategory.seo_name)
					: state.selectedCategory.seo_name,
				brand: undefined,
				model: undefined,
				color: undefined
			});
		}

		if (!state.userBuyoutCode) dispatch(ACTION_TYPES.RESET_APPRAISAL);
		await dispatch(ACTION_TYPES.LOAD_BRANDS);
	},
	async [ACTION_TYPES.LOAD_BRANDS]({ commit, dispatch, state }) {
		commit(MUTATION_TYPES.RESET_BRANDS);
		commit(MUTATION_TYPES.SET_LOADING_BRANDS, !state.loadingBrands);
		commit(MUTATION_TYPES.RESET_MODELS);
		commit(MUTATION_TYPES.RESET_COLORS);
		if (!state.userBuyoutCode) await dispatch(ACTION_TYPES.RESET_APPRAISAL);

		const { response, error } = await dispatch(ACTION_TYPES.LOAD_DATA, {
			url: `${URLS.PRODUCT_MANUFACTURERS}/${state.selectedCategory.seo_name}`
		});
		if (error) return;

		commit(MUTATION_TYPES.SET_BRANDS, response.data);
		commit(MUTATION_TYPES.SET_LOADING_BRANDS, !state.loadingBrands);

		if (
			config.isURLHandlingEnabled &&
			config.router.currentRoute.params.brand
		) {
			let sel_brand = state.brands.find(
				brand => brand.seo_name === config.router.currentRoute.params.brand
			);

			if (sel_brand) {
				commit(MUTATION_TYPES.SET_SELECTED_BRAND, sel_brand || null);
				if (!state.models.length) await dispatch(ACTION_TYPES.LOAD_MODELS);
			} else {
				commit(MUTATION_TYPES.RESET_MODELS);
				commit(MUTATION_TYPES.RESET_COLORS);

				if (!state.userBuyoutCode) await dispatch(ACTION_TYPES.RESET_APPRAISAL);

				if (config.isURLHandlingEnabled) {
					await changeRouteParam({
						brand: undefined,
						model: undefined,
						color: undefined
					});
				}
			}
		}
	},
	async [ACTION_TYPES.CHANGE_BRAND]({ commit, dispatch, state }) {
		if (!state.selectedBrand){
			commit(MUTATION_TYPES.SET_SELECTED_BRAND, null);
			commit(MUTATION_TYPES.SET_SELECTED_MODEL, null);
			commit(MUTATION_TYPES.SET_SELECTED_COLOR, null);
		}
		if (config.isURLHandlingEnabled) {
			await changeRouteParam({
				brand: state.selectedBrand? state.selectedBrand.seo_name: undefined,
				model: undefined,
				color: undefined
			});
		}

		if (!state.userBuyoutCode) await dispatch(ACTION_TYPES.RESET_APPRAISAL);

		await dispatch(ACTION_TYPES.LOAD_MODELS);
	},
	async [ACTION_TYPES.LOAD_MODELS]({ commit, dispatch, state }) {
		if(!state.selectedCategory || !state.selectedBrand) return;
		commit(MUTATION_TYPES.SET_LOADING_MODELS, !state.loadingModels);
		commit(MUTATION_TYPES.RESET_MODELS);
		commit(MUTATION_TYPES.RESET_COLORS);
		if (!state.userBuyoutCode) await dispatch(ACTION_TYPES.RESET_APPRAISAL);

		const { response, error } = await dispatch(ACTION_TYPES.LOAD_DATA, {
			url: `${URLS.PRODUCT_MODELS}/${state.selectedCategory.seo_name}/${state.selectedBrand.seo_name}`
		});
		if (error) return;

		commit(MUTATION_TYPES.SET_MODELS, response.data);
		commit(MUTATION_TYPES.SET_LOADING_MODELS, !state.loadingModels);

		if (
			config.isURLHandlingEnabled &&
			config.router.currentRoute.params.model
		) {
			let sel_model = state.models.find(
				model => model.seo_name === config.router.currentRoute.params.model
			);

			if (sel_model) {
				commit(MUTATION_TYPES.SET_SELECTED_MODEL, sel_model || null);
				if (!state.colors.length) await dispatch(ACTION_TYPES.LOAD_COLORS);
			} else {
				commit(MUTATION_TYPES.RESET_COLORS);
				if (!state.userBuyoutCode) await dispatch(ACTION_TYPES.RESET_APPRAISAL);
				if (config.isURLHandlingEnabled) {
					await changeRouteParam({
						model: undefined,
						color: undefined
					});
				}
			}
		}
	},
	async [ACTION_TYPES.CHANGE_MODEL]({ commit, dispatch, state }) {
		if(!state.selectedModel) commit(MUTATION_TYPES.RESET_COLORS);
		if (config.isURLHandlingEnabled) {
			await changeRouteParam({
				model: state.selectedModel?.seo_name ?? undefined,
				color: undefined
			});
		}
		if(!state.userBuyoutCode) dispatch(ACTION_TYPES.RESET_APPRAISAL);
		if(state.selectedModel) await dispatch(ACTION_TYPES.LOAD_COLORS);
	},
	async [ACTION_TYPES.LOAD_COLORS]({ commit, dispatch, state }) {
		commit(MUTATION_TYPES.SET_LOADING_COLORS, !state.loadingColors);
		commit(MUTATION_TYPES.RESET_COLORS);
		if (!state.userBuyoutCode) dispatch(ACTION_TYPES.RESET_APPRAISAL);
		const { response, error } = await dispatch(ACTION_TYPES.LOAD_DATA, {
			url: `${URLS.PRODUCT_COLORS}/${state.selectedModel.seo_name}`
		});
		if (error) return;

		commit(MUTATION_TYPES.SET_COLORS, response.data);
		commit(MUTATION_TYPES.SET_LOADING_COLORS, !state.loadingColors);

		if (
			config.isURLHandlingEnabled &&
			config.router.currentRoute.params.color
		) {
			let sel_color = state.colors.find(
				color => color.seo_name === config.router.currentRoute.params.color
			);

			if (sel_color) {
				commit(MUTATION_TYPES.SET_SELECTED_COLOR, sel_color || null);
			} else {
				if (!state.userBuyoutCode) dispatch(ACTION_TYPES.RESET_APPRAISAL);
				if (config.isURLHandlingEnabled) {
					await changeRouteParam({
						color: undefined
					});
				}
			}
		}
	},
	async [ACTION_TYPES.LOAD_GENDERS]({ commit, dispatch }) {
		const { response } = await dispatch(ACTION_TYPES.LOAD_DATA, {
			url: URLS.APPRAISAL_GENDERS
		});
		if (response) commit(MUTATION_TYPES.SET_GENDERS, response.data);
	},
	async [ACTION_TYPES.LOAD_STATES]({ commit, dispatch }) {
		const { response } = await dispatch(ACTION_TYPES.LOAD_DATA, {
			url: URLS.APPRAISAL_STATES
		});
		if (response) {
			commit(MUTATION_TYPES.SET_STATES, response.data);
		}
	},
	async [ACTION_TYPES.LOAD_QUALITY_TYPES]({ commit, dispatch }) {
		const { response } = await dispatch(ACTION_TYPES.LOAD_DATA, {
			url: URLS.BUYOUT_QUALITIES
		});
		if (response) commit(MUTATION_TYPES.SET_QUALITY_TYPES, response.data);
	},
	async [ACTION_TYPES.LOAD_APPRAISAL]({ commit, dispatch, state }) {
		commit(MUTATION_TYPES.SET_LOADING_APPRAISAL, true);
		const { response, error } = await dispatch(ACTION_TYPES.LOAD_DATA, {
			url: `${URLS.APPRAISAL}/${state.selectedColor.seo_name}`
		});

		if (response) {
			commit(MUTATION_TYPES.SET_APPRAISAL_LOAD_ERROR, null);
			commit(MUTATION_TYPES.SET_LOADING_APPRAISAL, false);
			commit(MUTATION_TYPES.SET_APPRAISAL, response.data);
			const qualities = _sortBy(response.data.appraisal_result, [function(o) { return o.price; }]).reverse();
			commit(MUTATION_TYPES.SET_QUALITIES, qualities);
		}

		if (error) {
			if(error.response) {
				commit(
					MUTATION_TYPES.SET_APPRAISAL_LOAD_ERROR,
					error.response.data.message
				);
			}
			else{
				console.error(error);
			}
			commit(MUTATION_TYPES.SET_LOADING_APPRAISAL, false);
		}
	},
	async [ACTION_TYPES.SEND_APPRAISAL](
		{ commit, state },
		{ payload, gaming = false, partner_edit = false }
	) {
		commit(MUTATION_TYPES.SET_DISABLE_BUYOUT_SEND_BTN, true);
		if (!config.isOnlyOnlineBuyout && !state.isBuyoutOnline) {
			let payloadOverride = {
				buyout_device: null,
				customer_data: null,
				partner: null,
				note: '',
				e_mail: state.preSaleInfoEmail
			}
			payload = { ...payload, ...payloadOverride }
		}
		if (state.userBuyoutCode)
			payload['user_buyout_code'] = state.userBuyoutCode;

		if (state.selectedNewModelPartner)
			payload['partner'] = state.selectedNewModelPartner;

		if (state.selectedBonus) {
			let bonus = state.selectedBonus;
			if (bonus['quality_name']) delete bonus['quality_name'];
			payload['buyout_bonus'] = bonus;
		}

		let url =
			config.isOnlyOnlineBuyout || state.isBuyoutOnline
				? URLS.BUYOUT
				: URLS.INFO_BUYOUT;
		if (gaming) url = URLS.BUYOUT_GAMING;
		try {
			const response = await apiAxios.post(url, payload, {headers: {'Accept-Language': i18n.locale}});

			if (response.status === 201)
				commit(
					MUTATION_TYPES.SET_USER_BUYOUT_CODE,
					response.data.user_buyout_code
				);

			commit(MUTATION_TYPES.SET_APPRAISAL_ERRORS, {});
			commit(MUTATION_TYPES.SET_IS_APPRAISAL_EDITABLE, false);
			commit(MUTATION_TYPES.SET_DISPLAY_THANK_YOU, state.isRegistration);

			commit(MUTATION_TYPES.SET_QUALITIES, [state.selectedQuality]);

			if (
				(state.isRegistration && !state.isDifferentPickupAddress) ||
				(!state.pickupAddress.street &&
					!state.pickupAddress.city &&
					!state.pickupAddress.post_code)
			)
				commit(MUTATION_TYPES.SET_PICKUP_ADDRESS, {
					street: state.contractForm.customer_data.street,
					city: state.contractForm.customer_data.city,
					post_code: state.contractForm.customer_data.postcode
				});

			if (state.isRegistration) {
				await sleep(500);
				VueScrollTo('#thankYouDialog', { offset: -150 });
			}

			if (partner_edit) {
				EventBus.$emit('showErrorMessage', {
					message: i18n.t(
						'buyout.AdditionalInfoForm.new_model_partner_was_saved'
					),
					is_error: false
				});
			}
		} catch (err) {
			if (err.response.status === 400) {
				commit(MUTATION_TYPES.SET_APPRAISAL_ERRORS, err.response.data);
			} else commit(MUTATION_TYPES.SET_COMMON_ERROR, true);
		} finally {
			commit(MUTATION_TYPES.SET_DISABLE_BUYOUT_SEND_BTN, false);
		}
	},
	async [ACTION_TYPES.GET_BUYOUT_BONUSES]({ commit, dispatch, state }) {
		const { response } = await dispatch(ACTION_TYPES.LOAD_DATA, {
			url: `${URLS.BONUS_DATA}/${state.campaignIdentifier}/${state.selectedColor.code}`
		});

		if (response && response.data.productBonusList.length)
			commit(MUTATION_TYPES.SET_BUYOUT_BONUSES, response.data.productBonusList);
	},
	async [ACTION_TYPES.LOAD_BUYOUT]({ commit, dispatch, state }) {
		const { response, error } = await dispatch(ACTION_TYPES.LOAD_DATA, {
			url: `${URLS.BUYOUT_INFO}/${state.userBuyoutCode}`,
			displayErrorNotification: false
		});
		if (error) {
			if (error.response.status === 404)
				commit(
					MUTATION_TYPES.SET_BUYOUT_LOAD_ERROR,
					i18n.t('buyout.error.buyout_not_found')
				);
			commit(MUTATION_TYPES.SET_BUYOUT_LOADING, false);
		} else {
			//set appraisal
			if (!state.buyoutLoading) commit(MUTATION_TYPES.SET_BUYOUT_LOADING, true);
			dispatch(ACTION_TYPES.RESET_APPRAISAL, state.userBuyoutCode);
			commit(MUTATION_TYPES.SET_APPRAISAL_ERRORS, {});
			commit(MUTATION_TYPES.SET_BUYOUT_LOAD_ERROR, null);

			if (!state.categories.length)
				await dispatch(ACTION_TYPES.LOAD_CATEGORIES);

			commit(
				MUTATION_TYPES.SET_SELECTED_CATEGORY_BY_SEO_NAME,
				response.data.product_type_choice.value
			);
			if (
				'additional_info' in response.data &&
				!_isEmpty(response.data.additional_info)
			) {
				for (const [key, value] of Object.entries(
					response.data.additional_info
				)) {
					commit(MUTATION_TYPES.SET_BUYOUT_ADDITIONAL_INFO_BY_KEY, {
						key: key,
						value: value
					});
				}
			}
			if (
				'birth_date' in response.data.customer_data &&
				response.data.customer_data['birth_date'] !== null
			) {
				response.data.customer_data['personal_identification_number'] =
					response.data.customer_data['birth_date'];
				delete response.data.customer_data['birth_date'];
			}
			if ('new_device_imei' in response.data.buyout_device) {
				commit(
					MUTATION_TYPES.SET_NEW_DEVICE_IMEI,
					response.data.buyout_device['new_device_imei']
				);
				commit(MUTATION_TYPES.SET_NEW_DEVICE_IMEI_SAVED, true);
				delete response.data.buyout_device['new_device_imei'];
			} else {
				commit(MUTATION_TYPES.SET_NEW_DEVICE_IMEI_SAVED, null);
			}

			if ('new_device_selected_model' in response.data.additional_info) {
				commit(
					MUTATION_TYPES.SET_SELECTED_NEW_MODEL,
					response.data.additional_info['new_device_selected_model']
				);
				delete response.data.additional_info['new_device_selected_model'];
			}

			if ('partner' in response.data) {
				commit(
					MUTATION_TYPES.SET_SELECTED_NEW_MODEL_PARTNER,
					response.data['partner']
				);
				delete response.data['partner'];
			}

			commit(MUTATION_TYPES.SET_CONTRACT_FORM, {
				customer_data: { ...response.data.customer_data },
				buyout_device: { ...response.data.buyout_device }
			});

			await dispatch(ACTION_TYPES.LOAD_BRANDS);
			commit(
				MUTATION_TYPES.SET_SELECTED_BRAND_BY_SEO_BANE,
				response.data.manufacturer_choice.value
			);
			await dispatch(ACTION_TYPES.LOAD_MODELS);
			commit(
				MUTATION_TYPES.SET_SELECTED_MODEL_BY_SEO_NAME,
				response.data.product_model_choice.value
			);
			await dispatch(ACTION_TYPES.LOAD_COLORS);
			commit(
				MUTATION_TYPES.SET_SELECTED_COLOR_BY_SEO_NAME,
				response.data.product_color_choice.value
			);
			commit(MUTATION_TYPES.SET_COLORS, [state.selectedColor]);

			commit(MUTATION_TYPES.SET_APPRAISAL, {
				device_conditions: response.data.device_conditions,
				appraisal_result: [response.data.appraisal_result],
				version: response.data.appraisal_version
			});

			commit(MUTATION_TYPES.SET_QUALITIES, [response.data.appraisal_result]);

			commit(
				MUTATION_TYPES.SET_DEFAULT_QUALITY_CONDITIONS,
				response.data.device_conditions
					.filter(cond => cond.type !== 'quality')
					.map(cond => cond.value)
			);
			await sleep(20);
			commit(
				MUTATION_TYPES.SET_SELECTED_QUALITY,
				response.data.appraisal_result
			);

			commit(
				MUTATION_TYPES.SET_BUYOUT_STATE_IDENTIFIER,
				response.data.type_state.identifier
			);
			commit(MUTATION_TYPES.SET_SELECTED_BONUS, response.data.buyout_bonus);

			EventBus.$emit('buyoutLoaded', {});

			commit(MUTATION_TYPES.SET_SELECTED_PARTNER, response.data.partner);

			commit(MUTATION_TYPES.SET_PICKUP_ADDRESS, {
				street: state.contractForm.customer_data.street,
				city: state.contractForm.customer_data.city,
				post_code: state.contractForm.customer_data.postcode
			});

			commit(MUTATION_TYPES.SET_BUYOUT_RECEIPT, response.data.receipt);
			commit(MUTATION_TYPES.SET_BUYOUT_LOADING, false);
		}
	},
	async [ACTION_TYPES.GET_BUYOUT_STATE]({ state, commit, dispatch }) {
		const { response } = await dispatch(
			ACTION_TYPES.LOAD_DATA,
			`${URLS.GET_BUYOUT_STATE}/${state.userBuyoutCode}`
		);
		commit(MUTATION_TYPES.SET_BUYOUT_STATE, response.data);
	},
	async [ACTION_TYPES.LOAD_PARTNERS](
		{ commit, dispatch, state },
		onlyMain = false
	) {
		let url = URLS.PARTNERS;
		let params = {}

		if (onlyMain) params['is_main'] = true;
		params['display_only_in_select_box'] = true;

		let { response } = await dispatch(ACTION_TYPES.LOAD_DATA, {
			url: `${url}/${state.campaignIdentifier}`,
			params: {
				params
			}
		});
		commit(MUTATION_TYPES.SET_PARTNERS, response ? response.data : []);
	},
	async [ACTION_TYPES.UPLOAD_RECEIPT]({ state, commit }, event) {
		try {
			let data = new FormData();
			data.append('receipt', event.target.files[0]);

			let response = await apiAxios.post(
				`${URLS.BUYOUT}/${URLS.UPLOAD_RECIEPT}/${state.userBuyoutCode}`,
				data,
				{
					headers: {
						'Content-Type': 'multipart/form-data',
						 'Accept-Language': i18n.locale
					}
				}
			);

			commit(MUTATION_TYPES.SET_BUYOUT_RECEIPT, response.data.receipt);
			commit(MUTATION_TYPES.SET_BUYOUT_RECEIPT_ERRORS, null);
		} catch (err) {
			if (err.response && err.response.status === 400) {
				commit(
					MUTATION_TYPES.SET_BUYOUT_RECEIPT_ERRORS,
					err.response.data.receipt.join(', ')
				);
			} else
				EventBus.$emit('showErrorMessage', { message: err, is_error: true });
		}
	},
	async [ACTION_TYPES.ORDER_PICKUP]({ commit, state }) {
		try {
			let address = state.pickupAddress;
			let response = await apiAxios.post(
				`${URLS.BUYOUT}/${URLS.PICKUP_ADDRESS}/${state.userBuyoutCode}`,
				{
					street: address.street,
					city: address.city,
					postcode: address.post_code,
					country_identifier: address.country_identifier
				},
				{headers: {'Accept-Language': i18n.locale}}
			);

			commit(
				MUTATION_TYPES.SET_BUYOUT_STATE_IDENTIFIER,
				response.data.typeState.identifier
			);
			commit(MUTATION_TYPES.SET_PICKUP_ADDRESS_ERRORS, null);
		} catch (err) {
			if (err.response && err.response.status === 400) {
				commit(MUTATION_TYPES.SET_PICKUP_ADDRESS_ERRORS, err.response.data);
			} else displayError(err);
		}
	},
	async [ACTION_TYPES.EDIT_IMEI]({ commit, state }) {
		try {
			await apiAxios.put(
				`${URLS.BUYOUT}/${URLS.ADDITIONAL_INFO}/${state.userBuyoutCode}`,
				{
					new_device_imei: state.newDeviceImei
				},{
					headers: {'Accept-Language': i18n.locale}
				}
			);
			commit(MUTATION_TYPES.SET_NEW_DEVICE_IMEI_ERRORS, {});
			commit(MUTATION_TYPES.SET_NEW_DEVICE_IMEI_SAVED, true);
			commit(MUTATION_TYPES.SET_EDITABLE_IMEI, false);
			EventBus.$emit('showErrorMessage', {
				message: state.campaign.new_device_imei_validation_required
					? i18n.t('buyout.AdditionalInfoForm.imei_was_saved')
					: i18n.t('buyout.AdditionalInfoForm.sn_was_saved'),
				is_error: false
			});
		} catch (err) {
			if (err.response && err.response.status === 400) {
				commit(MUTATION_TYPES.SET_NEW_DEVICE_IMEI_ERRORS, err.response.data);
			} else displayError(err);
			commit(MUTATION_TYPES.SET_NEW_DEVICE_IMEI_SAVED, false);
		}
	},
	async [ACTION_TYPES.EDIT_SELECTED_NEW_MODEL]({ commit, state }) {
		try {
			await apiAxios.put(
				`${URLS.BUYOUT}/${URLS.ADDITIONAL_INFO}/${state.userBuyoutCode}`,
				{
					new_device_selected_model: state.selectedNewModel
				},{
					headers: {'Accept-Language': i18n.locale}
				}
			);
			EventBus.$emit('showErrorMessage', {
				message: i18n.t('buyout.AdditionalInfoForm.new_model_was_saved'),
				is_error: false
			});
		} catch (err) {}
	},
	async [ACTION_TYPES.LOAD_STATE_TYPES]({ commit, dispatch }) {
		const { response } = await dispatch(ACTION_TYPES.LOAD_DATA, {
			url: URLS.BUYOUT_STATES
		});
		if (response) commit(MUTATION_TYPES.SET_STATE_TYPES, response.data);
	},
	[ACTION_TYPES.RESET_DEVICE_FINDER]({ commit }) {
		commit(MUTATION_TYPES.RESET_CATEGORIES);
		commit(MUTATION_TYPES.RESET_BRANDS);
		commit(MUTATION_TYPES.RESET_MODELS);
		commit(MUTATION_TYPES.RESET_COLORS);
	},
	[ACTION_TYPES.RESET_APPRAISAL]({ state }, userBuyoutCode = null) {
		state.appraisal = null;
		state.selectedQuality = null;
		state.isAppraisalEditable = true;
		state.qualities = [];
		state.userBuyoutCode = userBuyoutCode;
		state.canNotBeBuyout = false;
		state.isAppraisalEditable = true;
		state.appraisalErrors = {}
		state.appraisalResults = [];
		state.defaultQualityConditions = [];
		state.buyoutBonus = null;
		state.buyoutState = null;
		state.displayThankYou = false;
		state.selectedBonus = null;
		state.selectedPartner = null;
		state.selectedNewModelPartner = null;
		state.selectedNewModel = null;
	},
	[ACTION_TYPES.SET_APPRAISAL_RESULT]({ commit, state }, { index, value }) {
		// This is needed for correct reactivity
		Vue.set(state.appraisalResults, index, value);
		if(!value) return;

		if (!value.appraisal_result) {
			commit(MUTATION_TYPES.SET_CANNOT_BE_BUYOUT, true);
			commit(MUTATION_TYPES.SET_SELECTED_QUALITY, null);
			return;
		}

		if (value.appraisal_result && state.canNotBeBuyout) {
			let all_appraisals_valid = true;
			state.appraisalResults.forEach(function (obj) {
				if (obj && !obj.appraisal_result) all_appraisals_valid = false;
			});
			if (all_appraisals_valid) {
				commit(MUTATION_TYPES.SET_CANNOT_BE_BUYOUT, false);
			}
		}
	},
	[ACTION_TYPES.DETERMINE_SELECTED_QUALITY]({commit, state, getters}) {
		const res = state.appraisalResults.map(item => {
			return getters[GETTER_TYPES.GET_BUYOUT_APPRAISAL_RESULT_BY_ID][item?.appraisal_result]?.quality ?? null;
		});
		if(!state.appraisal) return;
		const quality_type_conditions = state.appraisal.device_conditions.filter(item => item.type === 'quality')
		const special_qualities = _filter(
			_map(
				_map(
					state.appraisal.device_conditions.filter(
						item => item.type === 'quality'
					), "choices[0].appraisal_result"
				),
				resultId => getters[GETTER_TYPES.GET_BUYOUT_APPRAISAL_RESULT_BY_ID][resultId]?.quality ?? null
			)
		)
    const selectedQualityDeviceConditions = _intersection(res.slice(0, quality_type_conditions.length), special_qualities);

    let quality;
    // is quality typed (A or A+) result?
    if (selectedQualityDeviceConditions.length) [quality] = selectedQualityDeviceConditions;
    else if (_includes(res.slice(quality_type_conditions.length), null)) // eslint-disable-line no-undefined
    // is selected any choice without result?
      return null; // eslint-disable-line nonblock-statement-body-position
    else [quality] = _reverse(_sortBy(_filter(_uniq(res))));

    let finalQuality = getters[GETTER_TYPES.QUALITIES].find(q => q.quality === quality);
		commit(MUTATION_TYPES.SET_SELECTED_QUALITY, finalQuality);
	},
}
